import { CardAdapter, CardFormInstance, walletError } from '../types'
import { resolveApiUrl } from '../../utils'
import { POST } from 'rest/request'

/**
 * Przelewy24 - formularz karty w iframe SDK operatora.
 */

const TOKENIZE_TIMEOUT = 60_000

let formCounter = 0

interface P24Event {
  p24?: boolean
  type?: string
  status?: boolean
  buttons?: string[]
  data?: Record<string, any>
  errors?: string[]
}

type CapturedListener = [any, any]

/**
 * SDK P24 zakłada nasłuch `message` na `window` w `render()` ORAZ przy KAŻDYM
 * `tokenize()` i nie zdejmuje żadnego z nich - w całym bundlu operatora nie ma ani
 * jednego `removeEventListener`, a instancja nie udostępnia metody sprzątającej.
 * Porzucone handlery reagują potem na start kolejnego iframe'a (SDK nie sprawdza
 * `event.source`) i strzelają `postMessage` do swojego, usuniętego już z DOM,
 * którego `contentWindow` jest wtedy `null`:
 *   Uncaught TypeError: Cannot read properties of null (reading 'postMessage')
 * - przy N-tym otwarciu formularza dokładnie N-1 razy.
 *
 * Referencji do ich funkcji nie dostajemy, ale rejestracja jest SYNCHRONICZNA
 * wewnątrz obu wywołań, więc wystarczy podmienić `window.addEventListener` na ten
 * jeden tick i zapamiętać, co przez niego przeszło. W tym oknie wykonuje się
 * wyłącznie kod SDK, więc nie przechwycimy cudzych nasłuchów.
 */
const captureSdkListeners = <T>(sink: CapturedListener[], call: () => T): T => {
  const original = window.addEventListener
  const patched: typeof window.addEventListener = (type: any, listener: any, options?: any) => {
    if (type === 'message') sink.push([listener, options])
    original.call(window, type, listener, options)
  }

  window.addEventListener = patched
  try {
    return call()
  } finally {
    // przywracamy TYLKO swoją podmianę - gdyby ktoś w międzyczasie nałożył własną,
    // zdjęcie jej byłoby gorsze niż zostawienie naszej
    if (window.addEventListener === patched) window.addEventListener = original
  }
}

const getErrors = (message: P24Event): string[] =>
  message.data?.errors || message.errors || []

export const przelewy24CardAdapter: CardAdapter = {
  create(cardForm, { lang = 'pl', onReady } = {}) {
    const fallbackId = `p24-card-form-${++formCounter}`

    let tokenization: any = null
    let destroyed = false
    let mountedElement: HTMLElement | null = null
    let listener: ((event: MessageEvent) => void) | null = null
    let pending: { resolve: (token: string) => void, reject: (error: Error) => void, timeout: any } | null = null

    /** nasłuchy założone przez SDK - zdejmujemy je za operatora, patrz captureSdkListeners */
    const sdkListeners: CapturedListener[] = []

    const detachListeners = () => {
      if (listener) window.removeEventListener('message', listener)
      listener = null
      sdkListeners.splice(0).forEach(([fn, options]) => window.removeEventListener('message', fn, options))
    }

    const settlePending = (error: Error | null, token?: string) => {
      if (!pending) return
      const current = pending
      pending = null
      clearTimeout(current.timeout)
      if (error) current.reject(error)
      else current.resolve(token as string)
    }

    const handleMessage = (event: MessageEvent) => {
      const message = event.data as P24Event
      if (!message || message.p24 !== true) return

      switch (message.type) {
        case 'ready': {
          // `status` jest na wierzchu payloadu ({p24, type, status, buttons}); czytany
          // z `data` dawał zawsze undefined, czyli "gotowe" niezależnie od formularza.
          // P24 wysyła to dopiero, gdy formularz RAZ stał się poprawny, i tłumi pierwsze
          // `status: false` - czyli blokada przycisku jest zawsze świadomą decyzją operatora.
          const status = message.status ?? message.data?.status
          onReady?.(status !== false)
          break
        }

        case 'success': {
          const refId = message.data?.refId
          if (refId) settlePending(null, refId)
          else settlePending(walletError('card_tokenize_failed'))
          break
        }

        case 'fail': {
          // kody operatora: regulationsIsRequired, invalidCardHolder, minWidth370px...
          const reason = getErrors(message)[0]
          if (pending) {
            settlePending(Object.assign(walletError('card_tokenize_failed'), { reason }))
            break
          }
          // `fail` bez trwającej tokenizacji to awaria SAMEGO formularza (błędna opcja
          // renderu, brak tłumaczeń) - SDK nie rzuca wtedy wyjątku, tylko rysuje martwy
          // iframe. Blokujemy przycisk, żeby klient nie czekał do timeoutu na klik w nic.
          console.error('[payments] Przelewy24: formularz zgłosił błąd przed tokenizacją:', reason || message)
          onReady?.(false)
          break
        }

        case 'loading':
        case 'start':
        case 'warning':
          // znane i celowo pomijane - stan formularza prowadzi `ready` i widoczność iframe'a
          break

        default:
          // Kontrakt wymienia loading / start / ready / success / fail, ale SDK bywa
          // bogatsze. W deweloperce podglądamy resztę - stąd wiadomo, czego naprawdę słuchać.
          if (process.env.NODE_ENV === 'development') {
            console.info('[payments] Przelewy24, nieobsługiwany event:', message)
          }
          break
      }
    }

    const instance: CardFormInstance = {
      async mount(element) {
        if (destroyed) return
        const sessionUrl = resolveApiUrl(cardForm.sessionUrl)
        if (!sessionUrl) throw walletError('card_session_url_missing')

        const session = await POST(sessionUrl, { body: { posId: cardForm.posId } })
        if (!session?.merchantId || !session?.sessionId || !session?.sign) {
          throw walletError('card_session_failed')
        }

        if (destroyed) return

        const Tokenization = window.Przelewy24CardTokenization
        if (typeof Tokenization !== 'function') throw walletError('card_sdk_not_loaded')

        // SDK waliduje merchantId jako LICZBĘ (string kończy się wyjątkiem
        // P24CardTokenizationIframeInvalidConfigurationException: merchantIdCanOnlyBeNumber),
        // a backend potrafi oddać go w cudzysłowie - posId jest stringiem w kontrakcie.
        const merchantId = Number(session.merchantId)
        if (!Number.isFinite(merchantId)) throw walletError('card_session_failed')

        if (!element.id) element.id = fallbackId
        // SDK P24 nie ma odpowiednika PayU-owego card.remove(), wiec kontener czyscimy sami;
        // bez tego ponowny montaz (zmiana jezyka, hot reload) doklada kolejny iframe
        element.innerHTML = ''
        mountedElement = element

        // ponowny montaż tej samej instancji nie może zostawić nasłuchów po poprzednim
        detachListeners()

        // nasłuch PRZED konstruktorem - iframe może odezwać się od razu po utworzeniu
        listener = handleMessage
        window.addEventListener('message', listener)

        tokenization = captureSdkListeners(sdkListeners, () =>
          new Tokenization(merchantId, String(session.sessionId), String(session.sign)))

        // Opcje trzymamy minimalne. Błędna wartość NIE przewraca się wyjątkiem: SDK łapie ją
        // sam i rysuje pusty iframe, który melduje `fail` - a instancja jest już wtedy zużyta
        // (`render()` jest jednorazowy), więc nie da się ponowić. Jeśli kiedyś podajemy własne
        // teksty, dozwolone klucze `labels` i `placeholders` to WYŁĄCZNIE:
        // card / exp / cvv / cardholder (inna nazwa = `<klucz>IsIncorrect`).
        // iframe wymaga min. 370 px szerokości - węższy kontener kończy się błędem minWidth370px
        try {
          captureSdkListeners(sdkListeners, () => tokenization.render('form', `#${element.id}`, { lang }))
        } catch (error) {
          console.error('[payments] Przelewy24: render formularza odrzucony', error)
          throw walletError('card_form_render_failed')
        }
      },

      tokenize(kind) {
        return new Promise<string>((resolve, reject) => {
          if (!tokenization) {
            reject(walletError('card_form_not_ready'))
            return
          }
          if (pending) {
            reject(walletError('card_tokenize_in_progress'))
            return
          }

          const tokenType = cardForm.tokenTypes?.[kind] || cardForm.tokenTypes?.single
          if (!tokenType) {
            reject(walletError('card_token_type_missing'))
            return
          }

          // wynik przyjdzie eventem; gdyby nie przyszedł, nie zostawiamy przycisku w spinnerze
          pending = {
            resolve,
            reject,
            timeout: setTimeout(() => settlePending(walletError('card_tokenize_timeout')), TOKENIZE_TIMEOUT)
          }

          try {
            const started = captureSdkListeners(sdkListeners, () => tokenization.tokenize(tokenType))

            // SDK zwraca promisę i ODRZUCA ją, gdy tokenizacja już trwa. Bez tego byłby to
            // nieobsłużony rejection, a przycisk wisiałby w spinnerze aż do timeoutu.
            // Rozwiązania promisy nie używamy - SDK spełnia ją PIERWSZĄ lepszą wiadomością
            // `p24` (także `loading`), więc jedynym wiarygodnym źródłem wyniku jest nasz nasłuch.
            Promise.resolve(started).catch(error => {
              console.error('[payments] Przelewy24: tokenizacja odrzucona przez SDK', error)
              settlePending(walletError('card_tokenize_in_progress'))
            })
          } catch (error) {
            console.error(error)
            settlePending(walletError('card_tokenize_failed'))
          }
        })
      },

      destroy() {
        if (destroyed) return
        destroyed = true
        settlePending(walletError('card_form_closed'))

        // najpierw nasłuchy (nasz i te po SDK), dopiero potem DOM - inaczej handler
        // operatora dostałby jeszcze zdarzenie z iframe'em wyrwanym spod nóg
        detachListeners()
        tokenization = null

        // iframe znika razem z adapterem - inaczej zostaje w DOM i nasluchuje dalej
        if (mountedElement) mountedElement.innerHTML = ''
        mountedElement = null
      }
    }

    return instance
  }
}
